import React from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../../components/layout/AppLayout';
import { dummyData } from './data';
import ProgressOverview from './ProgressOverview';
import StageSnapshot from './StageSnapshot';
import DelayedTasks from './DelayedTasks';
import Accountability from './Accountability';
import DashboardHeader from './DashboardHeader';
import MaterialStatus from './MaterialStatus';
import NextWeekPlan from './NextWeekPlan';
import PhotoHighlights from './PhotoHighlights';
import ExecutionSummary from './ExecutionSummary';
import { useProjectsList } from '../../../../../../contexts/ProjectsContext';
import moment from 'moment';
import { getOperationsWeeklyStats } from '../../../../services/api/implementation/impl';
import Loader from '../../../../../shared/components/ui/Loader';
import toast from 'react-hot-toast';
import ErrorScreen from '../../../../../shared/components/ui/ErrorScreen';

const getErrorMessage = (error) => {
    if (!error) return 'Unable to load weekly report.';
    const msg = error?.response?.data?.message || error?.message;
    if (msg && typeof msg === 'string') {
        const reportRunDateMatch = msg.match(/reportRunDate must be on or after (\d{4}-\d{2}-\d{2}) for WEEKLY reports/i);
        if (reportRunDateMatch) {
            const minDate = reportRunDateMatch[1];
            return `Weekly report is only available from ${minDate} onwards. Please select a week that ends on or after ${minDate} to view the report.`;
        }
        return msg;
    }
    return 'Unable to load weekly report. Please try again.';
};

const WeeklyReports = () => {
    const navigate = useNavigate();
    const { projectsForDropdown, projectsError } = useProjectsList();
    const projects = projectsForDropdown?.length > 0 ? projectsForDropdown : [{ label: "No Project", id: null, value: null }];
    const [currentProject, setCurrentProject] = React.useState(projects[0]?.label || "No Project");
    // Default to last day of previous week (current week not selectable)
    const [selectedDate, setSelectedDate] = React.useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - d.getDay() - 1);
        return d;
    });
    const [reportWeekText, setReportWeekText] = React.useState('');

    const [weeklyData, setWeeklyData] = React.useState(null);
    const [loadingStats, setLoadingStats] = React.useState(false);
    const [reportError, setReportError] = React.useState(null);

    // Check for project API error
    React.useEffect(() => {
        if (projectsError) {
            const message = projectsError?.response?.data?.message || projectsError?.message || 'Unable to load projects.';
            setReportError(message);
            toast.error(message);
        }
    }, [projectsError]);


    React.useEffect(() => {
        if (selectedDate) {
            // Report Week = the week the user selected (same as picker), not the +7 API week
            const start = new Date(selectedDate);
            start.setDate(start.getDate() - start.getDay()); // Sunday start of selected week
            const end = new Date(start);
            end.setDate(end.getDate() + 6); // Saturday end of selected week

            const options = { day: 'numeric', month: 'short' };
            const startYear = start.getFullYear();
            const endYear = end.getFullYear();

            const startStr = start.toLocaleDateString('en-GB', options);
            const endStr = end.toLocaleDateString('en-GB', options);

            const rangeText = startYear === endYear
                ? `${startStr} - ${endStr} ${endYear}`
                : `${startStr} ${startYear} - ${endStr} ${endYear}`;

            setReportWeekText(`Report Week: ${rangeText}`);
        }
    }, [selectedDate]);

    React.useEffect(() => {
        if (projects?.length > 0 && projects[0]?.id !== null && currentProject === "No Project") {
            setCurrentProject(projects[0]?.label);
        }
    }, [projects, currentProject]);

    const handleDateChange = (date) => {
        setSelectedDate(date);
    };

    const fetchStats = React.useCallback(async () => {
        const projectObj = projects.find(p => p.label === currentProject);
        let projectId = projectObj?.id;
        if (!projectId && projectObj?.value) projectId = projectObj.value;

        if (!projectId) return;

        setReportError(null);
        setLoadingStats(true);
        try {
            // Selected date in week W → send date in next week (W+1): add 7 days
            const apiDate = moment(selectedDate).add(7, 'days');
            const dateStr = apiDate.format('YYYY-MM-DD');
            const data = await getOperationsWeeklyStats(projectId, dateStr);
            setWeeklyData(data);
        } catch (error) {
            console.error("Failed to fetch weekly stats", error);
            const message = getErrorMessage(error);
            setReportError(message);
            setWeeklyData(null);
            toast.error(message);
        } finally {
            setLoadingStats(false);
        }
    }, [currentProject, selectedDate, projects]);

    React.useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    // Data Mapping
    const progressStats = weeklyData ? {
        planned: weeklyData.weeklyWorkBlock?.planned?.count || 0,
        done: weeklyData.weeklyWorkBlock?.done?.count || 0,
        delayed: weeklyData.weeklyWorkBlock?.delayedAtStart?.count + weeklyData.weeklyWorkBlock?.delayedDuringTask?.count || 0,
        completion: weeklyData.weeklyWorkBlock?.weeklyCompletionPercent || 0
    } : {
        planned: 0,
        done: 0,
        delayed: 0,
        completion: 0
    };

    const stagesData = weeklyData?.weeklySnapshotBlock?.stages?.map((s, i) => ({
        name: s.stage,
        plan: s.plannedPercent,
        actual: s.actualPercent,
        color: dummyData.stageColors?.[i % dummyData.stageColors.length] || '#1F4B55'
    })) || [];

    const delayedTasks = (Array.isArray(weeklyData?.topDelaysAndAlertsBlock?.tasks) ? weeklyData.topDelaysAndAlertsBlock.tasks : []).map((task, i) => ({
        id: task.id,
        title: task.name,
        cause: task.liveTask?.name || "Delayed",
        workType: task.liveTask?.workType?.name || "General",
        // Logic: "To Do" -> "Not Started Yet", "Review" -> "In Review", else actual status
        status: task.liveTask?.status === "To Do"
            ? "Not Started Yet"
            : (task.liveTask?.status === "Review" ? "In Review" : (task.liveTask?.status || "Pending")),
        person: `${task.responsibleUser?.name || "Unknown"}`,
        type: task.status === "Delayed" ? "Critical" : "High",
        color: dummyData.stageColors?.[i % dummyData.stageColors.length] || '#FF7676'
    }));

    const accountabilityData = weeklyData?.weeklyAccountabilityBlock?.map(person => ({
        id: person.id,
        role: person.designation?.name || "Role",
        name: person.name,
        initials: person.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase(),
        tasksCompleted: person.completed,
        totalTasks: person.total,
        percentage: person.avgQualityScore || 0,
        delayed: person.total - person.completed
    })) || [];

    const photoHighlightsData = weeklyData?.photosBlock?.tasks?.flatMap(task =>
        (task.images || []).map(img => ({
            id: img.id,
            title: task.name,
            time: moment(task.date).format('ddd, hh:mm A'),
            url: img.url
        }))
    ) || [];

    const nextWeekPlanData = weeklyData?.nextWeekPlanBlock?.tasks?.map((task, i) => ({
        id: task.id,
        title: task.name,
        tag: task.workType?.name || "Planned",
        tagType: "default",
        color: dummyData.stageColors?.[i % dummyData.stageColors.length] || '#D4B95E'
    })) || [];

    return (
        <Layout>
            <Loader isVisible={loadingStats} />
            <div className="bg-[#F8FAFC] min-h-screen">
                <div className="sticky top-0 z-40 bg-[#EEF3F7] backdrop-blur pt-6 px-6">
                    <DashboardHeader
                        projectName={currentProject}
                        onProjectChange={setCurrentProject}
                        lastUpdated={weeklyData?.lastUpdated || dummyData.lastUpdated}
                        date={selectedDate}
                        onDateChange={handleDateChange}
                        projects={projects}
                        reportWeekText={reportWeekText}
                        onViewSample={() => navigate('/builder/operations/weekly-reports/sample')}
                    />
                </div>
                <div className="px-6 pb-6 max-w-[1600px] mx-auto">
                    {reportError ? (
                        <ErrorScreen
                            title="Unable to Load Weekly Report"
                            message={reportError}
                            onRetry={() => window.location.reload()}
                            isRetrying={loadingStats}
                        />
                    ) : (
                        <div className="flex flex-col gap-6">
                            {/* Main Content Grid - Split into Left (2/3) and Right (1/3) blocks for independent vertical flow */}
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
                                {/* Left Block - Spans 2 Columns */}
                                <div className="lg:col-span-2 flex flex-col gap-6">
                                    {/* ProgressOverview */}
                                    <div>
                                        <ProgressOverview stats={progressStats} />
                                    </div>
                                    {/* Row for Delays and Materials */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <DelayedTasks tasks={delayedTasks} />
                                        </div>
                                        <div>
                                            <MaterialStatus materials={weeklyData?.materialStatsForWeek ?? []} />
                                        </div>
                                    </div>
                                    {/* Next Week's Plan */}
                                    <div>
                                        <NextWeekPlan plans={nextWeekPlanData} />
                                    </div>
                                    {/* Execution Summary */}
                                    {/* <div>
                                        <ExecutionSummary data={dummyData.executionSummary} />
                                    </div> */}
                                </div>

                                {/* Right Block - Spans 1 Column - Independent Stack */}
                                <div className="flex flex-col gap-6 lg:mt-0">
                                    <div>
                                        <StageSnapshot stages={stagesData} />
                                    </div>
                                    <div>
                                        <Accountability people={accountabilityData} />
                                    </div>
                                    <div>
                                        <PhotoHighlights photos={photoHighlightsData} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default WeeklyReports;